package uz.mint.frontend.i18n

/**
 * The two help documents the footer opens: how to install an eSIM, and which phones support one.
 *
 * They reuse [LegalDocument] because the reader overlay already renders exactly this shape —
 * eyebrow, intro, headed sections — and a second nearly identical type would be duplication for
 * its own sake. The `revision` field is that eyebrow line here rather than a legal revision.
 *
 * **Translated into all three languages, unlike [PublicOffer] and [PrivacyPolicy].** Those stay
 * Russian because a mistranslated refund clause is a legal problem. These are instructions: a
 * customer following them needs them in the language they read, and the cost of an awkward
 * phrase is an awkward phrase.
 *
 * The device list is deliberately **not exhaustive and says so**. New models appear constantly,
 * and a list presented as complete would read as "your phone is not supported" to someone whose
 * phone works fine — which is why every locale ends on the `*#06#` check, the only answer that
 * stays correct.
 */
fun installGuideFor(locale: Locale): LegalDocument = when (locale) {
    Locale.RU -> INSTALL_RU
    Locale.UZ -> INSTALL_UZ
    Locale.EN -> INSTALL_EN
}

fun compatiblePhonesFor(locale: Locale): LegalDocument = when (locale) {
    Locale.RU -> DEVICES_RU
    Locale.UZ -> DEVICES_UZ
    Locale.EN -> DEVICES_EN
}

// --- Installation -----------------------------------------------------------------------

private val INSTALL_RU = LegalDocument(
    revision = "Пошаговая инструкция",
    intro = "Установка занимает пару минут и делается один раз. Лучше всё сделать дома, пока есть " +
        "Wi-Fi: для загрузки профиля нужен интернет, а в поездке его может не оказаться.",
    sections = listOf(
        LegalDocument.Section(
            "1. Проверьте телефон",
            "Убедитесь, что телефон поддерживает eSIM и не заблокирован под одного оператора. " +
                "Список моделей — в разделе «Совместимые телефоны».",
        ),
        LegalDocument.Section(
            "2. Оплатите тариф",
            "Выберите страну и пакет, оплатите картой через Click. Сразу после оплаты QR-код " +
                "появится в разделе «Мои eSIM» в вашем профиле.",
        ),
        LegalDocument.Section(
            "3. Подключитесь к Wi-Fi",
            "Профиль скачивается из сети оператора, поэтому на время установки нужен интернет. " +
                "Подойдёт и мобильный интернет вашей обычной SIM.",
        ),
        LegalDocument.Section(
            "4. Отсканируйте QR-код — iPhone",
            "Настройки → Сотовая связь → Добавить eSIM → Использовать QR-код. Наведите камеру на " +
                "код и следуйте подсказкам. Если код открыт на этом же телефоне, нажмите «Ввести " +
                "данные вручную» и вставьте код активации.",
        ),
        LegalDocument.Section(
            "5. Отсканируйте QR-код — Android",
            "Настройки → Сеть и интернет → SIM-карты → Добавить eSIM (на разных прошивках пункт " +
                "может называться «Загрузить SIM-карту» или «Добавить тарифный план»). Дальше — " +
                "камера и QR-код.",
        ),
        LegalDocument.Section(
            "6. Назовите профиль",
            "Дайте eSIM понятное имя — например «Mint Турция». Так вы не перепутаете её со своей " +
                "основной SIM в настройках.",
        ),
        LegalDocument.Section(
            "7. По прилёте включите роуминг данных",
            "В настройках выберите профиль Mint как источник сотовых данных и включите для него " +
                "роуминг данных. Голосовые вызовы и SMS оставьте на основной SIM — пакет включает " +
                "только интернет.",
        ),
        LegalDocument.Section(
            "Если интернет не появился",
            "Перезагрузите телефон, проверьте, что выбран профиль Mint и что включён роуминг " +
                "данных. Сеть регистрируется до нескольких минут. Если не помогло — напишите нам " +
                "в Telegram, номер заказа под рукой.",
        ),
    ),
)

private val INSTALL_UZ = LegalDocument(
    revision = "Bosqichma-bosqich yoʻriqnoma",
    intro = "Oʻrnatish bir necha daqiqa vaqt oladi va bir marta bajariladi. Buni uyda, Wi-Fi " +
        "bor paytda qilgan maʼqul: profilni yuklab olish uchun internet kerak, safarda esa u " +
        "boʻlmasligi mumkin.",
    sections = listOf(
        LegalDocument.Section(
            "1. Telefonni tekshiring",
            "Telefoningiz eSIM'ni qoʻllab-quvvatlashiga va bitta operatorga bogʻlanmaganiga " +
                "ishonch hosil qiling. Modellar roʻyxati «Mos telefonlar» boʻlimida.",
        ),
        LegalDocument.Section(
            "2. Tarifni toʻlang",
            "Davlat va paketni tanlab, Click orqali karta bilan toʻlang. Toʻlovdan soʻng QR-kod " +
                "profilingizdagi «Mening eSIM'larim» boʻlimida paydo boʻladi.",
        ),
        LegalDocument.Section(
            "3. Wi-Fi'ga ulaning",
            "Profil operator tarmogʻidan yuklanadi, shuning uchun oʻrnatish vaqtida internet " +
                "kerak. Oddiy SIM'ingizdagi mobil internet ham boʻladi.",
        ),
        LegalDocument.Section(
            "4. QR-kodni skanerlang — iPhone",
            "Sozlamalar → Uyali aloqa → eSIM qoʻshish → QR-koddan foydalanish. Kamerani kodga " +
                "toʻgʻrilang va koʻrsatmalarga amal qiling. Agar kod shu telefonda ochilgan " +
                "boʻlsa, «Maʼlumotlarni qoʻlda kiritish»ni bosing.",
        ),
        LegalDocument.Section(
            "5. QR-kodni skanerlang — Android",
            "Sozlamalar → Tarmoq va internet → SIM-kartalar → eSIM qoʻshish (proshivkaga qarab " +
                "«SIM-kartani yuklab olish» yoki «Tarif rejasini qoʻshish» deb nomlanishi " +
                "mumkin). Keyin — kamera va QR-kod.",
        ),
        LegalDocument.Section(
            "6. Profilga nom bering",
            "eSIM'ga tushunarli nom qoʻying — masalan «Mint Turkiya». Shunda uni sozlamalarda " +
                "asosiy SIM bilan adashtirmaysiz.",
        ),
        LegalDocument.Section(
            "7. Yetib borgach data roumingni yoqing",
            "Sozlamalarda Mint profilini uyali maʼlumotlar manbasi sifatida tanlang va u uchun " +
                "data roumingni yoqing. Qoʻngʻiroq va SMS'ni asosiy SIM'da qoldiring — paketga " +
                "faqat internet kiradi.",
        ),
        LegalDocument.Section(
            "Agar internet ishlamasa",
            "Telefonni oʻchirib yoqing, Mint profili tanlanganini va data rouming yoqilganini " +
                "tekshiring. Tarmoqqa ulanish bir necha daqiqa davom etishi mumkin. Yordam " +
                "bermasa — buyurtma raqami bilan Telegram'da yozing.",
        ),
    ),
)

private val INSTALL_EN = LegalDocument(
    revision = "Step-by-step guide",
    intro = "Installation takes a couple of minutes and is done once. Do it at home while you " +
        "still have Wi-Fi: downloading the profile needs an internet connection, and you may " +
        "not have one when you land.",
    sections = listOf(
        LegalDocument.Section(
            "1. Check your phone",
            "Make sure your phone supports eSIM and is not carrier-locked. See the “Compatible " +
                "phones” section for the model list.",
        ),
        LegalDocument.Section(
            "2. Pay for a plan",
            "Pick a destination and a package, then pay by card through Click. The QR code " +
                "appears in “My eSIMs” in your profile right after payment.",
        ),
        LegalDocument.Section(
            "3. Connect to Wi-Fi",
            "The profile is downloaded from the carrier's network, so you need internet while " +
                "installing. Mobile data on your usual SIM works just as well.",
        ),
        LegalDocument.Section(
            "4. Scan the QR code — iPhone",
            "Settings → Cellular → Add eSIM → Use QR Code. Point the camera at the code and " +
                "follow the prompts. If the code is open on this same phone, tap “Enter Details " +
                "Manually” and paste the activation code.",
        ),
        LegalDocument.Section(
            "5. Scan the QR code — Android",
            "Settings → Network & internet → SIMs → Add eSIM (depending on the skin this may " +
                "read “Download a SIM instead” or “Add mobile plan”). Then camera and QR code.",
        ),
        LegalDocument.Section(
            "6. Name the profile",
            "Give the eSIM a label you will recognise — “Mint Turkey”, say. That keeps it apart " +
                "from your main SIM in settings.",
        ),
        LegalDocument.Section(
            "7. Turn on data roaming when you arrive",
            "In settings, select the Mint profile as your mobile data source and enable data " +
                "roaming for it. Leave calls and SMS on your main SIM — the package covers data " +
                "only.",
        ),
        LegalDocument.Section(
            "If there is no connection",
            "Restart the phone, check that the Mint profile is selected and that data roaming is " +
                "on. Registering on a network can take a few minutes. If that does not help, " +
                "message us on Telegram with your order number.",
        ),
    ),
)

// --- Device support ---------------------------------------------------------------------

private val DEVICES_RU = LegalDocument(
    revision = "Список неполный — проверьте свой телефон",
    intro = "Ниже — популярные модели с поддержкой eSIM. Новые выходят постоянно, поэтому список " +
        "не исчерпывающий: если вашей модели тут нет, это не значит, что она не подходит. " +
        "Надёжнее всего проверить самому — способ в конце страницы.",
    sections = listOf(
        LegalDocument.Section(
            "Apple iPhone",
            "iPhone XR, XS, XS Max и все более новые: 11, 12, 13, 14, 15, 16 и их версии Pro, " +
                "Pro Max, mini, Plus и SE (2-го поколения и новее). " +
                "Важно: iPhone, купленные в материковом Китае, eSIM не поддерживают, а " +
                "американские модели iPhone 14 и новее работают ТОЛЬКО с eSIM.",
        ),
        LegalDocument.Section(
            "Samsung",
            "Galaxy S20, S21, S22, S23, S24, S25 и их версии Plus и Ultra (кроме некоторых " +
                "региональных вариантов). Galaxy Z Flip и Z Fold всех поколений. " +
                "Galaxy Note 20 и Note 20 Ultra. Galaxy A54, A55.",
        ),
        LegalDocument.Section(
            "Google Pixel",
            "Pixel 3 и новее, включая версии a, Pro, Pro XL и Fold. " +
                "Pixel 3, купленные в Австралии, Тайване и Японии, eSIM не поддерживают.",
        ),
        LegalDocument.Section(
            "Xiaomi",
            "Xiaomi 12T Pro, 13, 13 Pro, 13 Lite, 14, 14 Pro, 15. Redmi Note 13 Pro+ (глобальная " +
                "версия). Poco X6 Pro. У Xiaomi поддержка сильно зависит от региона продажи.",
        ),
        LegalDocument.Section(
            "Huawei и Honor",
            "Huawei P40, P40 Pro, Mate 40 Pro. Honor Magic 4 Pro, Magic 5 Pro, Magic 6 Pro.",
        ),
        LegalDocument.Section(
            "Другие производители",
            "Motorola Razr (2019 и новее), Edge 40 и новее. OnePlus 11, 12, 13. " +
                "Oppo Find X3 Pro, X5, X6, X8. Nothing Phone (1), (2), (2a), (3). Sony Xperia " +
                "10 III Lite, 1 IV, 5 IV, 1 V. Rakuten Mini, Big, Hand.",
        ),
        LegalDocument.Section(
            "Как проверить наверняка",
            "Наберите на телефоне *#06#. Если в открывшемся окне помимо IMEI есть строка EID — " +
                "eSIM поддерживается. Ещё вариант: iPhone — Настройки → Сотовая связь, должен " +
                "быть пункт «Добавить eSIM»; Android — Настройки → Сеть и интернет → SIM-карты. " +
                "И отдельно убедитесь, что телефон не заблокирован под одного оператора: даже " +
                "с eSIM такой аппарат чужой профиль не примет.",
        ),
    ),
)

private val DEVICES_UZ = LegalDocument(
    revision = "Roʻyxat toʻliq emas — telefoningizni tekshiring",
    intro = "Quyida eSIM'ni qoʻllab-quvvatlaydigan mashhur modellar. Yangilari doimiy chiqadi, " +
        "shuning uchun roʻyxat toʻliq emas: modelingiz bu yerda boʻlmasa ham, u mos kelishi " +
        "mumkin. Eng ishonchlisi — oʻzingiz tekshirish, usuli sahifa oxirida.",
    sections = listOf(
        LegalDocument.Section(
            "Apple iPhone",
            "iPhone XR, XS, XS Max va undan yangilari: 11, 12, 13, 14, 15, 16 hamda ularning " +
                "Pro, Pro Max, mini, Plus va SE (2-avlod va undan yangi) versiyalari. " +
                "Muhim: materik Xitoyda sotib olingan iPhone'lar eSIM'ni qoʻllamaydi, AQSh " +
                "modellari iPhone 14 va undan yangilari esa FAQAT eSIM bilan ishlaydi.",
        ),
        LegalDocument.Section(
            "Samsung",
            "Galaxy S20, S21, S22, S23, S24, S25 va ularning Plus, Ultra versiyalari (baʼzi " +
                "mintaqaviy variantlardan tashqari). Galaxy Z Flip va Z Fold barcha avlodlari. " +
                "Galaxy Note 20 va Note 20 Ultra. Galaxy A54, A55.",
        ),
        LegalDocument.Section(
            "Google Pixel",
            "Pixel 3 va undan yangilari, jumladan a, Pro, Pro XL va Fold versiyalari. " +
                "Avstraliya, Tayvan va Yaponiyada sotib olingan Pixel 3 eSIM'ni qoʻllamaydi.",
        ),
        LegalDocument.Section(
            "Xiaomi",
            "Xiaomi 12T Pro, 13, 13 Pro, 13 Lite, 14, 14 Pro, 15. Redmi Note 13 Pro+ (global " +
                "versiya). Poco X6 Pro. Xiaomi'da qoʻllab-quvvatlash sotilgan mintaqaga koʻp " +
                "bogʻliq.",
        ),
        LegalDocument.Section(
            "Huawei va Honor",
            "Huawei P40, P40 Pro, Mate 40 Pro. Honor Magic 4 Pro, Magic 5 Pro, Magic 6 Pro.",
        ),
        LegalDocument.Section(
            "Boshqa ishlab chiqaruvchilar",
            "Motorola Razr (2019 va undan yangi), Edge 40 va yangilari. OnePlus 11, 12, 13. " +
                "Oppo Find X3 Pro, X5, X6, X8. Nothing Phone (1), (2), (2a), (3). Sony Xperia " +
                "10 III Lite, 1 IV, 5 IV, 1 V. Rakuten Mini, Big, Hand.",
        ),
        LegalDocument.Section(
            "Qanday aniq tekshirish mumkin",
            "Telefonda *#06# ni tering. Ochilgan oynada IMEI'dan tashqari EID qatori boʻlsa — " +
                "eSIM qoʻllab-quvvatlanadi. Yana bir usul: iPhone — Sozlamalar → Uyali aloqa, " +
                "«eSIM qoʻshish» boʻlishi kerak; Android — Sozlamalar → Tarmoq va internet → " +
                "SIM-kartalar. Alohida: telefon bitta operatorga bogʻlanmaganiga ishonch hosil " +
                "qiling — bunday apparat eSIM bilan ham begona profilni qabul qilmaydi.",
        ),
    ),
)

private val DEVICES_EN = LegalDocument(
    revision = "Not an exhaustive list — check your own phone",
    intro = "Below are popular eSIM-capable models. New ones ship constantly, so this list is " +
        "not exhaustive: if yours is missing, that does not mean it will not work. The most " +
        "reliable answer is to check the phone yourself — see the end of this page.",
    sections = listOf(
        LegalDocument.Section(
            "Apple iPhone",
            "iPhone XR, XS, XS Max and everything newer: 11, 12, 13, 14, 15, 16 and their Pro, " +
                "Pro Max, mini, Plus and SE (2nd generation onward) variants. " +
                "Note: iPhones bought in mainland China have no eSIM support, and US models of " +
                "the iPhone 14 and newer are eSIM-ONLY.",
        ),
        LegalDocument.Section(
            "Samsung",
            "Galaxy S20, S21, S22, S23, S24, S25 and their Plus and Ultra variants (some " +
                "regional versions excepted). Galaxy Z Flip and Z Fold, all generations. " +
                "Galaxy Note 20 and Note 20 Ultra. Galaxy A54, A55.",
        ),
        LegalDocument.Section(
            "Google Pixel",
            "Pixel 3 and newer, including the a, Pro, Pro XL and Fold variants. " +
                "Pixel 3 units sold in Australia, Taiwan and Japan have no eSIM support.",
        ),
        LegalDocument.Section(
            "Xiaomi",
            "Xiaomi 12T Pro, 13, 13 Pro, 13 Lite, 14, 14 Pro, 15. Redmi Note 13 Pro+ (global " +
                "version). Poco X6 Pro. On Xiaomi, support depends heavily on the sales region.",
        ),
        LegalDocument.Section(
            "Huawei and Honor",
            "Huawei P40, P40 Pro, Mate 40 Pro. Honor Magic 4 Pro, Magic 5 Pro, Magic 6 Pro.",
        ),
        LegalDocument.Section(
            "Other makers",
            "Motorola Razr (2019 onward), Edge 40 and newer. OnePlus 11, 12, 13. " +
                "Oppo Find X3 Pro, X5, X6, X8. Nothing Phone (1), (2), (2a), (3). Sony Xperia " +
                "10 III Lite, 1 IV, 5 IV, 1 V. Rakuten Mini, Big, Hand.",
        ),
        LegalDocument.Section(
            "How to check for certain",
            "Dial *#06# on the phone. If the panel shows an EID line alongside the IMEI, eSIM is " +
                "supported. Alternatively: on iPhone, Settings → Cellular should offer “Add " +
                "eSIM”; on Android, Settings → Network & internet → SIMs. Separately, make sure " +
                "the phone is not carrier-locked — a locked handset will refuse another " +
                "operator's profile even with eSIM hardware.",
        ),
    ),
)
